import { useEffect, useRef } from 'react'
import { hasSavedViewportForCity } from '../utils/mapViewport'

/**
 * Hook que hace flyTo cuando cambia la ciudad actual.
 * Resuelve el problema de que el mapa se inicializa una vez y no se re-centra.
 *
 * IMPORTANTE: maneja una condición de carrera donde `currentCity` se actualiza
 * inmediatamente (síncrono) pero `cityCenter` llega después (asíncrono, de la API).
 * Por eso una ciudad solo se considera "procesada" cuando ya tenemos su centro.
 *
 * Para evitar vuelos "atrasados" que compiten con el vuelo al popup (al hacer
 * click en un árbol, `skip` cambia de true a false), la ciudad+centro se
 * registran SIEMPRE en `lastFlownRef` aunque no se vuele.
 *
 * Además, si la sesión ya tiene un viewport guardado para la ciudad, no se
 * vuela: se respeta el zoom con el que el usuario dejó el mapa.
 *
 * @param {Object} map - Instancia del mapa de Mapbox
 * @param {boolean} mapLoaded - Estado de carga del mapa
 * @param {string} currentCity - Slug de la ciudad actual
 * @param {Array} cityCenter - [lng, lat] del centro de la ciudad
 * @param {number} cityZoom - Nivel de zoom de la ciudad
 * @param {Object} [options]
 * @param {boolean} [options.skip] - Omitir el vuelo al cantón (ej: el centrado lo
 *   gestiona el efecto del tenant, o hay un árbol/popup activo).
 */
export const useFlyToCity = (map, mapLoaded, currentCity, cityCenter, cityZoom, { skip = false } = {}) => {
  // Guardamos tanto la ciudad como el centro ya procesados.
  const lastFlownRef = useRef({ city: null, center: null })

  useEffect(() => {
    if (!map || !mapLoaded || !currentCity || !cityCenter) return

    const centerKey = cityCenter.join(',')
    const lastFlown = lastFlownRef.current
    if (lastFlown.city === currentCity && lastFlown.center === centerKey) return

    // Marcar SIEMPRE como procesada (aunque no volemos): evita el vuelo
    // "atrasado" que competiría con el popup cuando `skip` cambia tras un click.
    lastFlownRef.current = { city: currentCity, center: centerKey }

    if (skip) return
    // Respetar el zoom guardado en la sesión: no re-volar en recargas.
    if (hasSavedViewportForCity(currentCity)) return

    map.flyTo({
      center: cityCenter,
      zoom: cityZoom || 12,
      duration: 1500,
      essential: true,
    })
  }, [map, mapLoaded, currentCity, cityCenter, cityZoom, skip])
}
